/**
 * File Storage Types
 *
 * Types for local storage, file management, and example models.
 */

/**
 * Metadata about a stored model.
 */
export interface ModelMetadata {
  /** EnergyPlus version */
  version: string;
  /** Number of zones in the model */
  zoneCount: number;
  /** Number of surfaces in the model */
  surfaceCount: number;
  /** Number of total objects */
  objectCount: number;
}

/**
 * A model stored in IndexedDB.
 */
export interface StoredModel {
  /** Unique identifier */
  id: string;
  /** User-provided name */
  name: string;
  /** Serialized IDF content */
  content: string;
  /** Last modification timestamp */
  lastModified: Date;
  /** Creation timestamp */
  createdAt: Date;
  /** Base64 encoded thumbnail image */
  thumbnail?: string | undefined;
  /** Model metadata extracted from content */
  metadata: ModelMetadata;
}

/**
 * Result of an import operation.
 */
export interface ImportResult {
  /** Whether import was successful */
  success: boolean;
  /** Error messages (fatal errors that prevent import) */
  errors: ImportError[];
  /** Warning messages (non-fatal issues) */
  warnings: ImportWarning[];
  /** Whether import can proceed despite errors */
  canProceed: boolean;
  /** Number of parsed objects */
  objectCount?: number | undefined;
  /** Detected EnergyPlus version */
  version?: string | undefined;
  /** Original filename */
  filename?: string | undefined;
  /** File size in bytes */
  fileSize?: number | undefined;
  /** Parsed content (only if successful or canProceed) */
  content?: string | undefined;
}

/**
 * Import error with details.
 */
export interface ImportError {
  /** Error message */
  message: string;
  /** Line number if applicable */
  line?: number;
  /** Whether the error is recoverable */
  recoverable: boolean;
}

/**
 * Import warning (non-fatal).
 */
export interface ImportWarning {
  /** Warning message */
  message: string;
  /** Line number if applicable */
  line?: number;
  /** Warning type */
  type: 'unknown-object' | 'deprecated-field' | 'syntax' | 'other';
}

/**
 * Options for IDF export.
 */
export interface ExportOptions {
  /** Output format */
  format: 'idf' | 'imf';
  /** Sort objects by class name */
  sortObjects: boolean;
  /** Include comments in output */
  includeComments: boolean;
  /** Line ending style */
  lineEndings: 'unix' | 'windows';
}

/**
 * Default export options.
 */
export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  format: 'idf',
  sortObjects: false,
  includeComments: true,
  lineEndings: 'unix',
};

/**
 * Example model category.
 */
export type ExampleCategory = 'residential' | 'commercial' | 'hvac-examples' | 'tutorials';

/**
 * Example model complexity level.
 */
export type ComplexityLevel = 'simple' | 'medium' | 'complex';

/**
 * An example model in the library.
 */
export interface ExampleModel {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Description of the model */
  description: string;
  /** Category for filtering */
  category: ExampleCategory;
  /** Complexity level */
  complexity: ComplexityLevel;
  /** Feature tags */
  features: string[];
  /** Thumbnail URL or data URI */
  thumbnail: string;
  /** URL to IDF file */
  idfUrl: string;
  /** URL to weather file */
  weatherUrl: string;
}

/**
 * Weather file information.
 */
export interface WeatherFileInfo {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Weather file URL */
  url: string;
  /** Location name */
  location: string;
  /** Latitude */
  latitude: number;
  /** Longitude */
  longitude: number;
  /** Elevation in meters */
  elevation: number;
  /** Timezone offset from UTC */
  timezone: number;
  /** Data source (e.g., TMY3, IWEC) */
  source: string;
}

/**
 * Recent file entry.
 */
export interface RecentFile {
  /** Unique identifier (same as StoredModel id for saved files) */
  id: string;
  /** File name */
  name: string;
  /** When file was last opened */
  lastOpened: Date;
  /** Whether the file is from local storage or was imported */
  source: 'local' | 'imported';
  /** Thumbnail if available */
  thumbnail?: string | undefined;
}

/**
 * Auto-save status.
 */
export interface AutoSaveStatus {
  /** Whether auto-save is enabled */
  enabled: boolean;
  /** Last auto-save timestamp */
  lastSaved: Date | null;
  /** Whether there are unsaved changes */
  isDirty: boolean;
  /** Current model ID being tracked */
  modelId: string | null;
}

/**
 * Library weather file - simplified metadata for persistence.
 * This is a subset of WeatherFileMetadata from the weather module,
 * containing only the fields needed for syncing between pages.
 */
export interface LibraryWeatherFile {
  /** Unique identifier from the library */
  id: string;
  /** Display name */
  name: string;
  /** EPW file URL */
  url: string;
  /** Location details */
  location: {
    city: string;
    region: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    elevation: number;
    timezone: number;
  };
  /** Climate zone info */
  climateZone: {
    ashrae?: string;
    koppen?: string;
  };
  /** Source type (TMY3, IWEC, etc.) */
  sourceType: string;
}
